import { ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { TableToolbarRow } from '@/components/ui/table/table-toolbar';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';

interface CampaignsToolbarProps {
  onSearchChange: (value: string) => void;
  filterDefinitions: FilterDefinition[];
  filters: FilterItem[];
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (...keys: string[]) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  filterBadges: ReturnType<typeof buildFilterBadges>;
  openPillId: string | null;
  setOpenPillId: (id: string | null) => void;
  selectionActions?: ReactNode;
}

export const CampaignsToolbar = ({
  onSearchChange,
  filterDefinitions,
  filters,
  setFilter,
  removeFilter,
  resetFilters,
  activeFilterCount,
  filterBadges,
  openPillId,
  setOpenPillId,
  selectionActions,
}: CampaignsToolbarProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchChange = useDebouncedCallback(onSearchChange, 300, [onSearchChange]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearchChange(value);
  };

  return (
    <div className="mb-4 flex flex-col gap-4">
      <TableToolbarRow
        start={
          <SearchBar
            placeholder={t('campaigns.search_placeholder')}
            value={searchValue}
            onChange={handleSearchChange}
          />
        }
        center={selectionActions}
        end={
          <ComplexFilterCombobox
            filterDefinitions={filterDefinitions}
            filters={filters}
            setFilter={setFilter}
            removeFilter={removeFilter}
            activeFilterCount={activeFilterCount}
            onOpenActivePill={setOpenPillId}
            showBulkActions={false}
          />
        }
      />

      <ComplexFilterBar
        filters={filterBadges}
        openPillId={openPillId}
        onOpenPillChange={setOpenPillId}
        onClearAll={resetFilters}
        showBulkActions={false}
      />
    </div>
  );
};
